import { autocompletion } from "@codemirror/autocomplete";
import { BasicSetupOptions } from "@uiw/react-codemirror";
import { useCallback, useEffect, useId, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { jsonToPon, ponToJson } from "src/datasets/DatasetTable/utils";
import { Callout } from "src/design-system/Callout";

type PonEditorProps = {
  value: string;
  basicSetup?: BasicSetupOptions;
  omitValidation?: boolean;
  onChange?: (value: string) => void;
};

const format = (value: string) => {
  const jsonValue = ponToJson(value);
  try {
    const jsonValueFormatted = JSON.stringify(JSON.parse(jsonValue), null, 2);

    return jsonToPon(jsonValueFormatted);
  } catch {
    return value;
  }
};

const unformat = (value: string) => {
  const jsonValue = ponToJson(value);
  try {
    const jsonValueUnformatted = JSON.stringify(JSON.parse(jsonValue));

    return jsonToPon(jsonValueUnformatted);
  } catch {
    return value;
  }
};

const COMPLETION_EXTENSION = autocompletion({
  override: [() => null],
});

export const PonEditor: React.FC<PonEditorProps> = ({
  value: propValue,
  onChange,
  omitValidation,
  basicSetup,
}) => {
  const [value, setValue] = useState(() => format(propValue));
  const [error, setError] = useState<{
    message: string;
    line: number | undefined;
  } | null>(null);

  const id = useId();

  const validate = useCallback(
    (value: string | undefined) => {
      if (!value) return;

      try {
        JSON.parse(ponToJson(value));
      } catch (e: unknown) {
        if (e instanceof SyntaxError) {
          const lineNumberStr = e.message.match(/line (\d+)/)?.[1];

          setError({
            message: e.message,
            line: lineNumberStr ? parseInt(lineNumberStr, 10) : undefined,
          });
        } else {
          setError({
            message: "Invalid JSON format. Please correct the JSON format.",
            line: undefined,
          });
        }
      }
    },
    [setError],
  );

  useEffect(() => {
    setValue(format(propValue));
    // Validate on initial load and when propValue changes
    if (!omitValidation) {
      validate(propValue);
    }
  }, [propValue, omitValidation, validate]);

  const validateDebounced = useDebounceCallback(validate, 1000);

  return (
    <div className="relative flex h-full flex-col gap-y-2 overflow-hidden">
      {error && <Callout type="error">{error.message}</Callout>}
      <CodeEditor
        basicSetup={basicSetup}
        // Disable autocompletion
        completionExtension={COMPLETION_EXTENSION}
        dataLoc={`pon-editor-${id}`}
        errorHighlightedLine={error?.line}
        language="python"
        value={value}
        autoFocus
        highlightOnFocus
        onBlur={() => {
          const newValue = unformat(value) ?? "";
          if (newValue !== propValue) {
            onChange?.(newValue);
          }
        }}
        onChange={(value: string | undefined) => {
          setError(null);
          if (!omitValidation) {
            validateDebounced(value);
          }
          return setValue(value ?? "");
        }}
      />
    </div>
  );
};
